import { useState, useRef, useEffect, useCallback } from "react";
import { Search, Printer, History as HistoryIcon, Send, X, Sparkles, BookOpen, PenTool, MessageCircle, FileText, VolumeX, Layers, Trash2, ChevronRight, Download, Copy, RefreshCw, Image as ImageIcon } from "lucide-react";
import { toast, Toaster } from "sonner";
import { apiGenerate, apiDiagram, apiChat, apiListHistory, apiGetHistory, apiDeleteHistory } from "@/lib/api";
import { DOMAIN_META, FMAA_TERMS, FMAA_FORMULAS, COLORS } from "@/data/fmaa";

/* ───── Speech ───── */
function useSpeech() {
  const [speaking, setSpeaking] = useState(null);
  const speak = useCallback((id, text) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speaking === id) {
      setSpeaking(null);
      return;
    }
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.95;
    utt.pitch = 1;
    utt.lang = "en-US";
    utt.onstart = () => setSpeaking(id);
    utt.onend = () => setSpeaking(null);
    utt.onerror = () => setSpeaking(null);
    window.speechSynthesis.speak(utt);
  }, [speaking]);
  return { speaking, speak };
}

/* ───── Wave icon ───── */
const WaveIcon = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "flex-end", height: 16 }}>
    {[0,1,2,3].map(i => (
      <span key={i} className={`wave-bar ${active ? "playing" : ""}`}
        style={{ height: active ? 14 : [8,14,10,12][i] }} />
    ))}
  </span>
);

/* ───── TermCard ───── */
const TermCard = ({ t, color, speak, speaking, onContext }) => {
  const def = t.definition || t.def;
  const id = `${t.term}`;
  const playing = speaking === id;
  return (
    <article className="hk-tcard lift rise" data-testid={`term-card-${t.term}`}>
      <div className="hk-tcard-top" style={{ background: color }} />
      <div className="hk-tcard-body">
        <div className="hk-tcard-head-row">
          <h3 className="hk-tterm">{t.term}</h3>
          <button
            className={`hk-speak ${playing ? "active" : ""}`}
            onClick={() => { onContext && onContext(t.term); speak(id, `${t.term}. ${def}`); }}
            title={playing ? "Stop" : "Listen"}
            aria-label="Pronounce term"
            data-testid={`speak-${t.term}`}
          >
            {playing ? <VolumeX size={16} color="var(--gold)"/> : <WaveIcon active={false} />}
          </button>
        </div>
        <p className="hk-tdef">{def}</p>
        <div className="hk-tfooter">
          <span className="hk-ttag" style={{ background: color + "22", color }}>
            {t.category || (DOMAIN_META[t.domain]?.label.split(" ")[0])}
          </span>
          <span className="hk-tlevel">Level {t.level}</span>
        </div>
      </div>
    </article>
  );
};

/* ───── FormulaCard ───── */
const FormulaCard = ({ f }) => (
  <article className="hk-fcard lift rise" data-testid={`formula-card-${f.name || f.eq?.slice(0,12)}`}>
    <div className="hk-fhead">
      <h3 className="hk-fname">{f.name}</h3>
      <p className="hk-fdesc">{f.description || f.desc}</p>
    </div>
    <div className="hk-fbody">
      <div className="hk-feq">{f.equation || f.eq}</div>
      {(f.components || []).map((c, i) => (
        <div key={i} className="hk-fpart">
          <span className="hk-fsym">{c.symbol}</span>
          <span className="hk-flbl">{c.label}</span>
        </div>
      ))}
    </div>
  </article>
);

/* ───── ControlsBar ───── */
const ControlsBar = ({ search, setSearch, cats, catColors, activeCat, setActiveCat, view, setView, domainFilter, setDomainFilter, isDomain }) => (
  <div className="hk-controls no-print">
    <div className="hk-search">
      <Search size={15} className="hk-search-icon" />
      <input
        className="hk-search-input"
        data-testid="search-input"
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter terms and formulas…"
      />
    </div>
    <div className="hk-filter-btns">
      {isDomain ? (
        ["all","A","B","C","D","E"].map(d => (
          <button
            key={d}
            className={`hk-fbtn ${domainFilter === d ? "active" : ""}`}
            onClick={() => setDomainFilter(d)}
            style={domainFilter === d && d !== "all" ? { background: DOMAIN_META[d].color, borderColor: DOMAIN_META[d].color } : {}}
            data-testid={`domain-filter-${d}`}
          >
            {d === "all" ? "All" : `${d} · ${DOMAIN_META[d].label.split(" ")[0]}`}
          </button>
        ))
      ) : (
        <>
          <button
            className={`hk-fbtn ${activeCat === "all" ? "active" : ""}`}
            onClick={() => setActiveCat("all")}
            data-testid="cat-all"
          >All</button>
          {cats.map(c => (
            <button
              key={c}
              className={`hk-fbtn ${activeCat === c ? "active" : ""}`}
              onClick={() => setActiveCat(c)}
              style={activeCat === c ? { background: catColors[c], borderColor: catColors[c] } : {}}
              data-testid={`cat-${c}`}
            >{c}</button>
          ))}
        </>
      )}
    </div>
    <div className="hk-toggle">
      <button className={`hk-tbtn ${view === "all" ? "active" : ""}`} onClick={() => setView("all")} title="All" data-testid="view-all">
        <Layers size={13} /> All
      </button>
      <button className={`hk-tbtn ${view === "terms" ? "active" : ""}`} onClick={() => setView("terms")} title="Terms only" data-testid="view-terms">
        <BookOpen size={13} />
      </button>
      <button className={`hk-tbtn ${view === "formulas" ? "active" : ""}`} onClick={() => setView("formulas")} title="Formulas only" data-testid="view-formulas">
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 13, fontWeight: 700 }}>ƒ</span>
      </button>
    </div>
  </div>
);

/* ───── Main component ───── */
const EXAMPLE_SUBJECTS = ["CMA Part 1","USMLE Cardiology","Corporate Finance","Macroeconomics","Organic Chemistry","Statistics","Tax Accounting"];
const EXAMPLE_DIAGRAMS = ["Accounting Cycle","Cash Flow Statement","Cardiac Conduction","Photosynthesis","Cell Mitosis","Supply & Demand","Break-Even Chart","Krebs Cycle"];
const CHAT_SUGGESTIONS = ["Explain in Hindi","Real-world mortgage example","Explain like I'm 12","Show me the formula"];

export default function HikariEngine() {
  const [tab, setTab] = useState("gen"); // gen | fmaa | diag

  // Generator state
  const [genInput, setGenInput] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genError, setGenError] = useState("");
  const [genSearch, setGenSearch] = useState("");
  const [genCat, setGenCat] = useState("all");
  const [genView, setGenView] = useState("all");

  // FMAA state
  const [fmaaSearch, setFmaaSearch] = useState("");
  const [fmaaDomain, setFmaaDomain] = useState("all");
  const [fmaaView, setFmaaView] = useState("all");

  // Diagram state
  const [diagInput, setDiagInput] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [diagError, setDiagError] = useState("");

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: "ai", text: "Hi! Ask me anything about any term — Hindi translation, real-world context, worked examples, anything." },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [contextTerm, setContextTerm] = useState("");
  const chatEndRef = useRef(null);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);

  const diagSvgRef = useRef(null);

  const { speaking, speak } = useSpeech();

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  /* ── Generator ── */
  async function generate(subject) {
    const q = (subject || genInput).trim();
    if (!q) return;
    if (subject) setGenInput(subject);
    setGenLoading(true);
    setGenError("");
    setGenResult(null);
    setGenSearch("");
    setGenCat("all");
    setGenView("all");
    try {
      const data = await apiGenerate(q);
      setGenResult(data);
      toast.success("Dictionary ready");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Generation failed.";
      setGenError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Generation failed");
    } finally {
      setGenLoading(false);
    }
  }

  /* ── Diagram ── */
  async function generateDiagram(concept) {
    const q = (concept || diagInput).trim();
    if (!q) return;
    if (concept) setDiagInput(concept);
    setDiagLoading(true);
    setDiagError("");
    setDiagResult(null);
    try {
      const data = await apiDiagram(q);
      setDiagResult(data);
      toast.success("Diagram ready");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Drawing failed.";
      setDiagError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Drawing failed");
    } finally {
      setDiagLoading(false);
    }
  }

  /* ── Chat ── */
  async function sendChat(prefilled) {
    const msg = (prefilled || chatInput).trim();
    if (!msg || chatLoading) return;
    setChatInput("");
    setChatMessages(m => [...m, { role: "user", text: msg }]);
    setChatLoading(true);
    try {
      const reply = await apiChat(msg, contextTerm, chatMessages);
      setChatMessages(m => [...m, { role: "ai", text: reply }]);
    } catch {
      setChatMessages(m => [...m, { role: "ai", text: "Something went wrong. Please try again." }]);
    } finally {
      setChatLoading(false);
    }
  }

  /* ── History ── */
  async function openHistory() {
    setHistoryOpen(true);
    try {
      const items = await apiListHistory();
      setHistoryItems(items);
    } catch {
      toast.error("Could not load history");
    }
  }

  async function loadHistoryItem(item) {
    try {
      const doc = await apiGetHistory(item.id);
      if (doc.kind === "dictionary") {
        setTab("gen");
        setGenResult(doc.payload);
        setGenError("");
      } else {
        setTab("diag");
        setDiagResult(doc.payload);
        setDiagError("");
      }
      setHistoryOpen(false);
    } catch {
      toast.error("Could not load item");
    }
  }

  async function deleteHistoryItem(e, id) {
    e.stopPropagation();
    try {
      await apiDeleteHistory(id);
      setHistoryItems(items => items.filter(i => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  function handlePrint() { window.print(); }

  /* ── Diagram export helpers ── */
  function _sanitiseFilename(s) {
    return (s || "diagram").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
  }
  function _getFullSvg() {
    if (!diagSvgRef.current) return null;
    const svg = diagSvgRef.current.querySelector("svg");
    if (!svg) return null;
    const clone = svg.cloneNode(true);
    if (!clone.getAttribute("xmlns")) clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    return new XMLSerializer().serializeToString(clone);
  }
  function copyDiagramSvg() {
    const s = _getFullSvg();
    if (!s) return;
    navigator.clipboard.writeText(s).then(() => toast.success("SVG copied")).catch(() => toast.error("Copy failed"));
  }
  function downloadSvg() {
    const s = _getFullSvg();
    if (!s) return;
    const blob = new Blob([s], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${_sanitiseFilename(diagResult?.title)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("SVG downloaded");
  }
  function downloadPng() {
    const s = _getFullSvg();
    if (!s) return;
    const svgEl = diagSvgRef.current.querySelector("svg");
    const vb = (svgEl.getAttribute("viewBox") || "0 0 1000 700").split(" ").map(Number);
    const w = vb[2] || svgEl.clientWidth || 1000;
    const h = vb[3] || svgEl.clientHeight || 700;
    const scale = 2;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FAFAF7";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${_sanitiseFilename(diagResult?.title)}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("PNG downloaded");
      }, "image/png");
    };
    img.onerror = () => toast.error("PNG export failed");
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(s);
  }

  /* ── Derived: Generator filters ── */
  const genCats = genResult ? [...new Set(genResult.terms.map(t => t.category))] : [];
  const genCatColors = {};
  genCats.forEach((c, i) => { genCatColors[c] = COLORS[i % COLORS.length]; });

  const filteredGenTerms = genResult ? genResult.terms.filter(t => {
    const catMatch = genCat === "all" || t.category === genCat;
    const q = genSearch.toLowerCase();
    const searchMatch = !q || (t.term + " " + t.definition).toLowerCase().includes(q);
    return catMatch && searchMatch && genView !== "formulas";
  }) : [];
  const filteredGenFormulas = genResult ? (genResult.formulas || []).filter(f => {
    const catMatch = genCat === "all" || f.category === genCat;
    const q = genSearch.toLowerCase();
    const searchMatch = !q || (f.name + " " + (f.description || "")).toLowerCase().includes(q);
    return catMatch && searchMatch && genView !== "terms";
  }) : [];

  /* ── Derived: FMAA filters ── */
  const filteredFmaaTerms = FMAA_TERMS.filter(t => {
    const domMatch = fmaaDomain === "all" || t.domain === fmaaDomain;
    const q = fmaaSearch.toLowerCase();
    const searchMatch = !q || (t.term + " " + t.def).toLowerCase().includes(q);
    return domMatch && searchMatch && fmaaView !== "formulas";
  });
  const filteredFmaaFormulas = FMAA_FORMULAS.filter(f => {
    const domMatch = fmaaDomain === "all" || f.domain === fmaaDomain;
    const q = fmaaSearch.toLowerCase();
    const searchMatch = !q || (f.name + " " + f.desc).toLowerCase().includes(q);
    return domMatch && searchMatch && fmaaView !== "terms";
  });

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />

      {/* NAV */}
      <nav className="hk-nav no-print" data-testid="main-nav">
        <div className="hk-brand">
          <span className="hk-brand-dot" />
          HIKARI <span className="hk-brand-gold">·</span> LABS
        </div>
        <div className="hk-nav-tabs">
          <button className={`hk-nav-tab ${tab === "gen" ? "active" : ""}`} onClick={() => setTab("gen")} data-testid="tab-gen">
            Generator
          </button>
          <button className={`hk-nav-tab ${tab === "fmaa" ? "active" : ""}`} onClick={() => setTab("fmaa")} data-testid="tab-fmaa">
            FMAA Ref
          </button>
          <button className={`hk-nav-tab ${tab === "diag" ? "active" : ""}`} onClick={() => setTab("diag")} data-testid="tab-diag">
            Diagrams
          </button>
        </div>
        <div className="hk-nav-right">
          <button className="hk-icon-btn" onClick={openHistory} data-testid="history-btn">
            <HistoryIcon size={13} /> History
          </button>
          <button className="hk-icon-btn" onClick={handlePrint} data-testid="print-btn">
            <Printer size={13} /> PDF
          </button>
        </div>
      </nav>

      {/* ───── GENERATOR ───── */}
      {tab === "gen" && (
        <>
          <section className="hk-hero no-print">
            <div className="hk-hero-inner rise">
              <div className="hk-hero-tag"><Sparkles size={12} /> Powered by Claude · Hikari Labs</div>
              <h1 className="hk-h1">
                Any <em>Subject</em>. <br />
                Instant <span className="hk-underline">Dictionary</span>.
              </h1>
              <p className="hk-hero-sub">
                Enter any exam or topic. Get a full visual dictionary + formula stack, alphabetised, colour-coded, printable.
              </p>
              <div className="hk-input-row">
                <input
                  className="hk-input"
                  data-testid="gen-input"
                  value={genInput}
                  onChange={(e) => setGenInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && generate()}
                  placeholder="e.g. Real Estate Finance · CMA Part 1 · USMLE Cardiology…"
                />
                <button
                  className="hk-btn-primary"
                  onClick={() => generate()}
                  disabled={genLoading}
                  data-testid="gen-btn"
                >
                  {genLoading ? "Generating…" : <>Generate <ChevronRight size={15} /></>}
                </button>
              </div>
              <div className="hk-chips">
                {EXAMPLE_SUBJECTS.map(s => (
                  <button key={s} className="hk-chip" onClick={() => generate(s)} data-testid={`example-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          </section>

          {genResult && !genLoading && (
            <ControlsBar
              search={genSearch} setSearch={setGenSearch}
              cats={genCats} catColors={genCatColors}
              activeCat={genCat} setActiveCat={setGenCat}
              view={genView} setView={setGenView}
              isDomain={false}
            />
          )}

          <main className="hk-main print-page">
            {genError && (
              <div className="hk-error" data-testid="gen-error">
                <strong>Generation failed.</strong> {genError}
              </div>
            )}

            {genLoading && (
              <div className="hk-skeleton">
                <div className="hk-spinner" />
                <p className="hk-empty-p" style={{ marginTop: 16 }}>Claude is writing your dictionary…</p>
              </div>
            )}

            {!genResult && !genError && !genLoading && (
              <div className="hk-empty" data-testid="gen-empty">
                <div className="hk-empty-icon"><BookOpen size={40} strokeWidth={1.5} /></div>
                <h2 className="hk-empty-h">Enter a subject above</h2>
                <p className="hk-empty-p">Claude will generate 30+ alphabetised terms and all key formulas.</p>
              </div>
            )}

            {genResult && (
              <div className="rise">
                <div className="hk-result-banner no-print">
                  <div>
                    <h2 className="hk-result-title">{genResult.subject} Dictionary</h2>
                    <p className="hk-result-sub">{genResult.subtitle}</p>
                  </div>
                  <div className="hk-result-stats">
                    <div className="hk-result-stat"><strong>{genResult.terms?.length || 0}</strong> Terms</div>
                    <div className="hk-result-stat"><strong>{genResult.formulas?.length || 0}</strong> Formulas</div>
                    <div className="hk-result-stat"><strong>{genCats.length}</strong> Categories</div>
                  </div>
                </div>

                {genCats.map(cat => {
                  const terms = filteredGenTerms.filter(t => t.category === cat);
                  const formulas = filteredGenFormulas.filter(f => f.category === cat);
                  if (!terms.length && !formulas.length) return null;
                  const col = genCatColors[cat];
                  return (
                    <section key={cat} data-testid={`cat-section-${cat}`}>
                      <div className="hk-dom-head">
                        <div className="hk-dom-badge" style={{ background: col }}>{cat.charAt(0).toUpperCase()}</div>
                        <div className="hk-dom-title">{cat}</div>
                        <div className="hk-dom-weight">{terms.length} terms · {formulas.length} formulas</div>
                      </div>

                      {terms.length > 0 && (
                        <div className="hk-grid" style={{ marginBottom: 20 }}>
                          {terms.map((t, i) => (
                            <TermCard key={i} t={t} color={col} speak={speak} speaking={speaking}
                              onContext={setContextTerm} />
                          ))}
                        </div>
                      )}

                      {formulas.length > 0 && (
                        <>
                          <div className="hk-section-head">
                            <span>Formulas</span>
                            <span className="hk-section-line" />
                          </div>
                          <div className="hk-grid">
                            {formulas.map((f, i) => <FormulaCard key={i} f={f} />)}
                          </div>
                        </>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </main>
        </>
      )}

      {/* ───── FMAA ───── */}
      {tab === "fmaa" && (
        <>
          <section className="hk-hero no-print">
            <div className="hk-hero-inner rise">
              <div className="hk-hero-tag"><FileText size={12} /> IMA Certified · Official CSO · March 2024</div>
              <h1 className="hk-h1">
                FMAA <em>Complete</em> <br />
                <span className="hk-underline">Reference</span>
              </h1>
              <div className="hk-ref-meta">
                {[[String(FMAA_TERMS.length),"Terms"],[String(FMAA_FORMULAS.length),"Formulas"],["5","Domains"],["80","MCQ"]].map(([n,l]) => (
                  <div key={l} className="hk-ref-stat">
                    <span className="hk-ref-num">{n}</span>
                    <span className="hk-ref-lbl">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <ControlsBar
            search={fmaaSearch} setSearch={setFmaaSearch}
            cats={[]} catColors={{}}
            activeCat={"all"} setActiveCat={() => {}}
            view={fmaaView} setView={setFmaaView}
            domainFilter={fmaaDomain} setDomainFilter={setFmaaDomain}
            isDomain={true}
          />

          <main className="hk-main print-page">
            {(fmaaDomain === "all" ? ["A","B","C","D","E"] : [fmaaDomain]).map(d => {
              const meta = DOMAIN_META[d];
              const terms = filteredFmaaTerms.filter(t => t.domain === d);
              const formulas = filteredFmaaFormulas.filter(f => f.domain === d);
              if (!terms.length && !formulas.length) return null;
              return (
                <section key={d} data-testid={`fmaa-domain-${d}`}>
                  <div className="hk-dom-head">
                    <div className="hk-dom-badge" style={{ background: meta.color }}>{d}</div>
                    <div className="hk-dom-title">{meta.label}</div>
                    <div className="hk-dom-weight">{meta.weight}</div>
                  </div>
                  {terms.length > 0 && (
                    <div className="hk-grid" style={{ marginBottom: 20 }}>
                      {terms.sort((a, b) => a.term.localeCompare(b.term)).map((t, i) => (
                        <TermCard key={i} t={t} color={meta.color} speak={speak} speaking={speaking}
                          onContext={setContextTerm} />
                      ))}
                    </div>
                  )}
                  {formulas.length > 0 && (
                    <>
                      <div className="hk-section-head">
                        <span>Formulas</span>
                        <span className="hk-section-line" />
                      </div>
                      <div className="hk-grid">
                        {formulas.map((f, i) => <FormulaCard key={i} f={f} />)}
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </main>
        </>
      )}

      {/* ───── DIAGRAM ───── */}
      {tab === "diag" && (
        <>
          <section className="hk-hero no-print">
            <div className="hk-hero-inner rise">
              <div className="hk-hero-tag"><PenTool size={12} /> SVG Engine · Hikari Labs</div>
              <h1 className="hk-h1">
                Any <em>Concept</em>. <br />
                Instant <span className="hk-underline">Diagram</span>.
              </h1>
              <p className="hk-hero-sub">
                Enter any topic — anatomy, accounting flows, chemistry, physics, processes. Get a printable, labelled diagram.
              </p>
              <div className="hk-input-row">
                <input
                  className="hk-input"
                  data-testid="diag-input"
                  value={diagInput}
                  onChange={(e) => setDiagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && generateDiagram()}
                  placeholder="e.g. Cardiac conduction system · Accounting cycle · Photosynthesis…"
                />
                <button
                  className="hk-btn-primary"
                  onClick={() => generateDiagram()}
                  disabled={diagLoading}
                  data-testid="diag-btn"
                >
                  {diagLoading ? "Drawing…" : <>Generate Diagram <ChevronRight size={15} /></>}
                </button>
              </div>
              <div className="hk-chips">
                {EXAMPLE_DIAGRAMS.map(s => (
                  <button key={s} className="hk-chip" onClick={() => generateDiagram(s)} data-testid={`diag-example-${s}`}>{s}</button>
                ))}
              </div>
            </div>
          </section>

          <main className="hk-main print-page">
            {diagError && (
              <div className="hk-error" data-testid="diag-error">
                <strong>Drawing failed.</strong> {diagError}
              </div>
            )}

            {diagLoading && (
              <div className="hk-skeleton">
                <div className="hk-spinner" />
                <p className="hk-empty-p" style={{ marginTop: 16 }}>Claude is drawing your diagram with Sonnet 4.5… up to 40s for richer detail.</p>
              </div>
            )}

            {!diagResult && !diagError && !diagLoading && (
              <div className="hk-empty" data-testid="diag-empty">
                <div className="hk-empty-icon"><PenTool size={40} strokeWidth={1.5} /></div>
                <h2 className="hk-empty-h">Enter any concept above</h2>
                <p className="hk-empty-p">Claude will draw a clean, labelled educational SVG diagram ready to print or save as PDF.</p>
              </div>
            )}

            {diagResult && (
              <div className="hk-diag-card rise" data-testid="diagram-result">
                <div className="hk-diag-head">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h2 className="hk-diag-title">{diagResult.title}</h2>
                    <p className="hk-diag-desc">{diagResult.description}</p>
                  </div>
                  <div className="hk-diag-actions no-print">
                    <button className="hk-diag-btn" onClick={() => generateDiagram(diagInput || diagResult.title)} title="Regenerate" data-testid="diag-regen">
                      <RefreshCw size={14} /> Regenerate
                    </button>
                    <button className="hk-diag-btn" onClick={() => copyDiagramSvg()} title="Copy SVG" data-testid="diag-copy">
                      <Copy size={14} /> Copy SVG
                    </button>
                    <button className="hk-diag-btn" onClick={() => downloadSvg()} title="Download SVG" data-testid="diag-dl-svg">
                      <Download size={14} /> SVG
                    </button>
                    <button className="hk-diag-btn" onClick={() => downloadPng()} title="Download PNG" data-testid="diag-dl-png">
                      <ImageIcon size={14} /> PNG
                    </button>
                  </div>
                </div>
                <div
                  className="hk-diag-body svg-frame"
                  ref={diagSvgRef}
                  dangerouslySetInnerHTML={{ __html: diagResult.svg }}
                />
              </div>
            )}
          </main>
        </>
      )}

      {/* ───── CHAT FAB ───── */}
      <button className="hk-fab no-print" onClick={() => setChatOpen(o => !o)} data-testid="chat-fab" aria-label="Chat">
        <MessageCircle size={22} />
      </button>

      <div className={`hk-chat-panel no-print ${chatOpen ? "" : "hidden"}`} data-testid="chat-panel">
        <div className="hk-chat-head">
          <div style={{ flex: 1 }}>
            <div className="hk-chat-title">Ask the Dictionary</div>
            <div className="hk-chat-sub">
              {contextTerm ? `Context: ${contextTerm}` : "Hindi · Real-world examples · Anything"}
            </div>
          </div>
          <button className="hk-chat-close" onClick={() => setChatOpen(false)} aria-label="Close chat" data-testid="chat-close">
            <X size={18} />
          </button>
        </div>

        <div className="hk-chat-msgs dark-scroll">
          {chatMessages.map((m, i) => (
            <div key={i} className={`hk-chat-msg ${m.role}`}>
              <div className="hk-chat-avatar">
                {m.role === "user" ? "U" : <Sparkles size={13} />}
              </div>
              <div className="hk-chat-bubble">{m.text}</div>
            </div>
          ))}
          {chatLoading && (
            <div className="hk-chat-msg ai">
              <div className="hk-chat-avatar"><Sparkles size={13} /></div>
              <div className="hk-chat-bubble" style={{ opacity: 0.7 }}>Thinking…</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {chatMessages.length === 1 && (
          <div className="hk-chat-sugs">
            {CHAT_SUGGESTIONS.map(s => (
              <button key={s} className="hk-chat-sug" onClick={() => setChatInput(contextTerm ? `${s} — ${contextTerm}` : s)} data-testid={`chat-sug-${s}`}>{s}</button>
            ))}
          </div>
        )}

        <div className="hk-chat-input-row">
          <textarea
            className="hk-chat-input"
            data-testid="chat-input"
            placeholder="Ask anything…"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
          />
          <button
            className="hk-chat-send"
            disabled={chatLoading || !chatInput.trim()}
            onClick={() => sendChat()}
            data-testid="chat-send"
            aria-label="Send"
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* ───── HISTORY DRAWER ───── */}
      <div className={`hk-drawer-overlay no-print ${historyOpen ? "open" : ""}`} onClick={() => setHistoryOpen(false)} />
      <aside className={`hk-drawer no-print ${historyOpen ? "open" : ""}`} data-testid="history-drawer">
        <div className="hk-drawer-head">
          <HistoryIcon size={18} color="var(--gold)" />
          <div className="hk-drawer-title">History</div>
          <button className="hk-chat-close" onClick={() => setHistoryOpen(false)} aria-label="Close" data-testid="history-close" style={{ color: "var(--muted)" }}>
            <X size={18} />
          </button>
        </div>
        <div className="hk-drawer-body dark-scroll">
          {historyItems.length === 0 ? (
            <div className="hk-empty" style={{ padding: 40 }}>
              <div className="hk-empty-icon"><HistoryIcon size={32} strokeWidth={1.5} /></div>
              <p className="hk-empty-p">Nothing saved yet.<br />Generate a dictionary or diagram to fill this up.</p>
            </div>
          ) : (
            historyItems.map(item => (
              <div key={item.id} className="hk-hist-item" onClick={() => loadHistoryItem(item)} data-testid={`history-item-${item.id}`}>
                <span className="hk-hist-kind">{item.kind}</span>
                <div className="hk-hist-title">{item.title}</div>
                {item.subtitle && <div className="hk-hist-sub">{item.subtitle}</div>}
                <div className="hk-hist-row">
                  <span className="hk-hist-date">{fmtDate(item.created_at)}</span>
                  <button className="hk-hist-del" onClick={(e) => deleteHistoryItem(e, item.id)} aria-label="Delete" data-testid={`history-del-${item.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
}
